import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { TYPE_STYLES, STATUS_LABELS } from "@/lib/api";
import { Calendar, User, Loader2, AlertCircle } from "lucide-react";

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function ResourceCard({ resource }) {
  const style = TYPE_STYLES[resource.resource_type] || TYPE_STYLES.web_page;
  const isLoading = ["pending", "extracting", "distilling"].includes(
    resource.status,
  );
  const isFailed = resource.status === "failed";

  return (
    <Link
      to={`/r/${resource.id}`}
      data-testid={`resource-card-${resource.id}`}
      className="card-hover group block bg-white border border-zinc-200 rounded-xl p-6 hover:border-zinc-400 hover:shadow-md flex flex-col gap-4 h-full"
    >
      <div className="flex items-start justify-between gap-3">
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border ${style.bg} ${style.text} ${style.border}`}
        >
          {style.label}
        </span>
        {isLoading && (
          <span
            data-testid={`status-${resource.id}`}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-500"
          >
            <Loader2 size={12} className="animate-spin" />
            {STATUS_LABELS[resource.status]}
          </span>
        )}
        {isFailed && (
          <span
            data-testid={`status-${resource.id}`}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600"
          >
            <AlertCircle size={12} />
            Fallito
          </span>
        )}
      </div>

      <div className="flex-1 flex flex-col gap-2">
        <h3 className="font-display text-lg font-medium leading-snug line-clamp-2 group-hover:text-[#002FA7] transition-colors">
          {resource.title || resource.source_url}
        </h3>
        {resource.subtitle && (
          <p className="text-sm text-zinc-600 line-clamp-2 leading-relaxed">
            {resource.subtitle}
          </p>
        )}
        {isFailed && resource.error && (
          <p className="text-xs text-red-600 line-clamp-2">{resource.error}</p>
        )}
      </div>

      {resource.keywords && resource.keywords.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {resource.keywords.slice(0, 5).map((kw) => (
            <Badge
              key={kw}
              variant="secondary"
              className="font-normal text-[11px] bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
            >
              {kw}
            </Badge>
          ))}
          {resource.keywords.length > 5 && (
            <span className="text-[11px] text-zinc-400 self-center">
              +{resource.keywords.length - 5}
            </span>
          )}
        </div>
      )}

      <div className="flex items-center gap-4 text-xs text-zinc-500 pt-3 border-t border-zinc-100">
        <span className="inline-flex items-center gap-1">
          <Calendar size={12} /> {formatDate(resource.created_at)}
        </span>
        {resource.author && (
          <span className="inline-flex items-center gap-1 truncate">
            <User size={12} /> {resource.author}
          </span>
        )}
      </div>
    </Link>
  );
}
